/**
 * The client app's words (docs/52 §3, §5, §7), US English, under the house voice rules: no dashes, no Oxford
 * comma, no semicolons, no exclamation marks, contractions, no "not just", no "from X to Y", and the docs/52
 * §7.1 vocabulary (never "GBP", "ticket", "10DLC", "deploy"). `pnpm lint:voice` and copy.test.ts check every leaf.
 *
 * Keys started life in apps/app/src/web/strings/en.ts (WP-PWA) and are kept as the contract; WP-COPY owns the
 * words. Templated strings are functions with typed arguments so a missing value is a compile error.
 */
import { list, n } from '../format.ts';

export const S = {
  app: {
    name: 'Found First',
    skip: 'Skip to content',
    loading: 'Loading your account',
    stillLoading: 'Still loading. Your connection may be slow.',
    updating: 'Updating',
    updated: (when: string) => `Updated ${when}`,
    refresh: 'Refresh',
    back: 'Back',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    done: 'Done',
    next: 'Next',
    tryAgain: 'Try again',
    seeAll: 'See all',
    pickOne: 'Tap anything in the list and it opens here.',
    open: 'Open',
    more: 'More',
    new: 'New',
    change: 'Change',
    edit: 'Edit',
    remove: 'Remove',
    yes: 'Yes',
    no: 'No',
    info: (what: string) => `What "${what}" means`,
    offline: "You're offline. You can still read everything.",
    offlineQueued: (count: number) => `You're offline. ${n(count, 'thing', 'things')} will send when you're back online.`,
    backOnline: "You're back online.",
    queuedSending: (count: number) => `Sending ${n(count, 'thing', 'things')} you added offline`,
    queuedFailed: "Something you added offline didn't send.",
    update: 'A new version of the app is ready.',
    updateAction: 'Refresh',
    stateDone: 'Done',
    stateCurrent: 'Now',
    stateTodo: 'Next',
    notFoundTitle: "We couldn't find that",
    notFoundBody: "It may have been removed, or the link is from another account. Nothing's wrong with your account.",
    goHome: 'Go to Home',
    errorTitle: "We couldn't load this",
    errorBody: "Nothing was lost. Check your connection and try again. If it keeps happening, tell us in a request.",
    offlineEmpty: "You're offline and we don't have a saved copy of this screen yet. It'll load when you're back online.",
    browserOld: 'Your browser is too old for the app. Update it and come back. Your email alerts keep working.',
    whereFrom: (src: string) => `From ${src}`,
    copy: 'Copy',
    copied: 'Copied',
  },
  nav: {
    label: 'Main',
    home: 'Home',
    leads: 'Leads',
    reviews: 'Reviews',
    jobs: 'Jobs',
    inbox: 'Inbox',
    account: 'Account',
    notifications: 'Notifications',
    unread: (count: number) => `${count} unread`,
    waiting: (count: number) => `${count} waiting on you`,
    location: 'Location',
    allLocations: 'All locations',
    switchBusiness: 'Switch business',
    yourAccount: 'Your account',
  },
  sources: {
    site: 'your website',
    google: 'Google',
    reviews: 'your Google reviews',
    us: 'our team',
    stripe: 'Stripe',
    grid: 'our map check',
  },
  signin: {
    title: 'Sign in',
    lead: "Enter the email you use with us. We'll send you a code and a link.",
    email: 'Email',
    emailError: 'Enter your email like name@yourbusiness.com',
    send: 'Send my code',
    newHere: "New here? You'll get access when you start with us.",
    checkTitle: 'Check your email',
    checkLead: (masked: string) => `We sent a code to ${masked}. It works for 15 minutes.`,
    codeLabel: 'Your six digit code',
    digit: (i: number) => `Digit ${i} of 6`,
    verify: 'Sign in',
    useLink: 'Open the link in the email instead. It signs you in on this phone.',
    resend: 'Send a new code',
    resendIn: (s: number) => `You can ask for a new code in ${s} seconds.`,
    resent: 'We sent a new code.',
    wrongEmail: 'Use a different email',
    linkTitle: 'Sign in to Found First',
    linkLead: 'Tap the button to finish signing in on this device.',
    linkButton: 'Sign in',
    linkNeedCode: (masked: string) => `This link opened on a different phone or browser. Enter the code we emailed to ${masked} instead.`,
    // Straight after paying (Checkout's success page): they're in, and the sign in link is on its way.
    paidTitle: "You're in",
    paidSending: 'Sending your sign in link',
    paidLead: (masked: string) => `Your payment went through. We sent a sign in link and a code to ${masked}. They work for 15 minutes.`,
    paidAskLead: "Your payment went through. Enter the email you paid with and we'll send your sign in link.",
    paidSendFailed: "We couldn't send your sign in link just now. Check the email below and try again.",
    passkeyTitle: 'Sign in faster next time',
    passkeyLead: 'Use Face ID or your fingerprint instead of a code. Your fingerprint never leaves your phone.',
    passkeyOn: 'Turn on Face ID',
    passkeyLater: 'Not now',
    passkeyDone: "Done. Next time you'll sign in with your face or fingerprint.",
    passkeyFailed: "That didn't work on this phone. You can keep using the emailed code.",
    signedOut: "You've been signed out.",
    noAccessTitle: "You're signed in, but not to a business yet",
    noAccessBody: "Ask the owner to invite you from Account, then Team. If you think this is wrong, email us at hello@winworkdigital.com.",
  },
  stepUp: {
    title: "Confirm it's you",
    lead: 'For your safety, we check again before changes like this.',
    passkey: 'Use Face ID or fingerprint',
    email: 'Email me a code',
    sent: (masked: string) => `We sent a code to ${masked}.`,
    confirm: 'Confirm',
  },
  install: {
    title: 'Put us on your home screen',
    lead: "That's how new leads can reach your phone the second they come in. It takes 20 seconds.",
    show: 'Show me how',
    later: 'Maybe later',
    installNow: 'Add to home screen',
    iosSteps: ['Tap the Share button at the bottom of Safari.', 'Scroll down and tap Add to Home Screen.', 'Tap Add. Then open Found First from your home screen.'],
    iosNotSafari: 'On iPhone this only works in Safari. Open this page in Safari first.',
    androidSteps: ['Tap the three dots at the top right of Chrome.', 'Tap Install app or Add to Home screen.', 'Open Found First from your home screen.'],
    shareIcon: 'The Share button looks like a square with an arrow pointing up.',
    done: "You're set. Open the app from your home screen from now on.",
  },
  push: {
    cardTitle: 'Lead alerts on this phone',
    turnOn: 'Turn on lead alerts',
    turnOff: 'Turn off on this phone',
    on: 'Lead alerts are on for this phone.',
    off: 'Get a buzz the moment a new lead comes in. We still email you every lead.',
    denied: 'Lead alerts are off on this phone. To turn them back on, open your phone Settings, find Found First, tap Notifications and allow them.',
    needsInstall: 'On iPhone, lead alerts work once the app is on your home screen.',
    unsupported: "This browser can't show alerts. We'll email you every lead instead.",
    working: 'Turning on',
    failed: "We couldn't turn on alerts. Nothing changed. Try again in a minute.",
  },
  home: {
    title: 'Home',
    greeting: (part: string, name: string | null) => (name ? `Good ${part}, ${name}.` : `Good ${part}.`),
    since: (when: string) => `Since ${when}`,
    sinceSummary: (leads: number, reviews: number, jobs: number) => {
      const bits = [leads ? n(leads, 'new lead', 'new leads') : null, reviews ? n(reviews, 'new review', 'new reviews') : null, jobs ? n(jobs, 'job published', 'jobs published') : null].filter(Boolean) as string[];
      if (!bits.length) return 'Nothing new since your last look. We’re still working.';
      if (bits.length === 1) return `${bits[0]}.`;
      return `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}.`;
    },
    newSince: 'New since your last visit',
    needsYou: 'Needs you',
    month: 'Your month',
    monthOf: (m: string) => `So far in ${m}`,
    lastMonth: (v: number) => `Last month ${v}`,
    noLastMonth: 'First month',
    metric: {
      site_leads: 'Leads from your site',
      google_calls: 'Calls from Google',
      new_reviews: 'New reviews',
      jobs_published: 'Jobs published',
      call_taps: 'Taps on Call',
    } as Record<string, string>,
    metricInfo: {
      site_leads: ['Leads from your site', 'People who filled in a form on your website this month. Spam is never counted.', 'Where it’s from: your website, counted the moment each form arrives.'],
      google_calls: ['Calls from Google', 'Taps on Call in your Google profile this month.', 'Where it’s from: Google. It updates about once a day and runs 2 or 3 days behind. It can’t tell us if the call connected.'],
      new_reviews: ['New reviews', 'New Google reviews this month, of any star rating.', 'Where it’s from: your Google profile, checked every hour.'],
      jobs_published: ['Jobs published', 'Jobs you sent us that are now live on your site or your Google profile.', 'Where it’s from: our records. Each one links out to the live post.'],
      call_taps: ['Taps on Call', 'Times someone pressed your phone number on your website.', 'Where it’s from: your website. We can’t see whether the call connected.'],
    } as Record<string, [string, string, string]>,
    map: 'Your map',
    mapLine: (top: number, total: number, keyword: string) => `You're in Google's top three on ${top} of ${total} points for "${keyword}".`,
    mapDayOne: (n0: number) => `On day one it was ${n0}.`,
    mapSee: 'See your map',
    mapInfo: ['Your map', 'We search Google from 81 points spread across the area you work in. Each point shows where you came up.', 'It isn’t your whole business. It’s one search, measured the same way every month so you can compare.'],
    whatWeDid: 'Activity',
    whatWeDidAll: 'See all activity',
    quick: 'Quick actions',
    quickActions: { ask_review: 'Ask for a review', add_job: 'Add a job', change_hours: 'Change hours', new_request: 'New request' } as Record<string, string>,
    nothingNeedsYou: "Nothing needs you right now. We'll put anything we need here.",
    activityTitle: 'Activity',
    activityLead: 'What your team did and what we delivered, newest first. Tap any line to see it live.',
    activityEmpty: 'Nothing here yet. Your first update lands in your first week.',
    seeIt: 'See it',
    /** c133: the quick ask on Home. An email is all it needs. */
    quickAsk: {
      title: 'Ask for a review',
      lead: "Finished a job? Type their email and we'll send the ask.",
      more: 'More ways to ask',
    },
    /** Deck rulings c012 and c042: "your two jobs" as a weekly habit over the 90 days after go-live. */
    twoJobs: {
      title: 'Your two jobs',
      lead: 'Ask every customer for a review and add job photos in the app.',
      reviewWeeks: (done: number) => `Review asks: ${done} of 12 weeks`,
      photoMonths: (done: number) => `Job photos: ${done} of 3 months`,
      reviewTarget: 'Aim for 10 of the 12.',
      photoTarget: 'Aim for 2 of the 3.',
      week: (w: number) => (w > 12 ? 'Last few days' : `Week ${w} of 12`),
      month: (m: number) => `Month ${m} of 3`,
      askedThisWeek: 'You asked for a review this week.',
      notAskedThisWeek: 'No review ask yet this week.',
      photosThisMonth: 'You added photos this month.',
      noPhotosThisMonth: 'No job photos yet this month.',
      ends: (d: string) => `Your day 90 check is on ${d}.`,
      ask: 'Ask for a review',
      addPhotos: 'Add job photos',
      /** c133: what counted this week, asks made and taps on the tracked review link. */
      thisWeek: (asks: number, taps: number) => `This week: ${n(asks, 'ask', 'asks')} and ${n(taps, 'link tap', 'link taps')}.`,
      /** Round 2 ruling r01: a week or month with no jobs is excused. */
      noJobsWeek: 'No jobs this week',
      noJobsMonth: 'No jobs this month',
      noJobsLastWeek: 'No jobs last week',
      noJobsLastMonth: 'No jobs last month',
      markedWeek: 'You marked this week as no jobs. It counts as kept.',
      markedMonth: 'You marked this month as no jobs. It counts as kept.',
      noJobsHint: "Had no jobs? Mark the week or month and it won't count against you.",
      marks: (weeks: number, months: number) => `Marked as no jobs: ${list([...(weeks ? [n(weeks, 'week', 'weeks')] : []), ...(months ? [n(months, 'month', 'months')] : [])])}.`,
      markWeek: (w: number) => `Week ${w}`,
      markMonth: (m: number) => `Month ${m}`,
      undo: 'Undo',
      undoLabel: (what: string) => `Undo the no jobs mark for ${what.toLowerCase()}`,
      marked: 'Marked. It counts as kept.',
      unmarked: 'Mark taken off.',
      info: [
        'Your two jobs',
        'For your day 90 check, we look for a review ask in at least 10 of your first 12 weeks and job photos in at least 2 of your first 3 months.',
        'Where it’s from: the review asks and photos you send through the app.',
      ] as [string, string, string],
    },
  },
  getLive: {
    title: (left: number) => (left === 0 ? "You're ready to go live." : left === 1 ? "You're 1 step from going live." : `You're ${left} steps from going live.`),
    ring: (done: number, total: number) => `${done} of ${total} of your steps done`,
    nextUp: (what: string) => `Next up: ${what}.`,
    waitingOnYou: 'Waiting on you',
    weAreOnIt: "We're on it",
    goLiveBy: (d: string) => `Your site goes live by ${d}.`,
    waited: (days: number) => `Time waiting on you so far: ${n(days, 'day', 'days')}.`,
    /** The 7 day clock (deck rulings c007, c008, c041). */
    clock: {
      title: 'Your 7 day clock',
      notStarted: 'Your 7 day clock starts the day your domain is connected.',
      used: (days: number) => `${n(days, 'day', 'days')} of 7 used`,
      paused: (days: number) => (days ? `Paused for ${n(days, 'day', 'days')} while we waited on you` : 'No pauses so far'),
      deadline: (d: string) => `Your site goes live by ${d}.`,
      pausedNow: 'The clock is paused while we wait on you.',
      /** The onboarding view's `pausedDays`, to one decimal. */
      pausedSoFar: (days: number) => `Paused ${days === 1 ? '1 day' : `${days} days`} so far while we waited on you. They're added to your date.`,
      pausedFor: {
        brand: 'The clock is paused until you check your site.',
        go_live: 'The clock is paused until you approve go-live.',
        photos: 'The clock is paused until you add your photos.',
        // D-b (29 Sept): every step only the client can clear pauses it.
        brand_corrections: 'The clock is paused until you send your changes to the new round.',
        licence: 'The clock is paused until you send us your license.',
      } as Record<string, string>,
      info: [
        'Your 7 day clock',
        'It starts the day your domain is connected. It pauses while we wait on you: your site check after 48 hours, your changes to a new round, your go-live OK after 24 hours, your photos and a license only you can send.',
        'Paused days are added to your date, so you never lose them.',
      ] as [string, string, string],
    },
    preview: 'See your new site',
    stuck: 'Stuck? Book 10 minutes with us',
    ask: 'Ask a question',
    helper: 'Invite someone to help',
    welcomeTitle: (name: string | null) => (name ? `You're in, ${name}.` : "You're in."),
    welcomeLead: 'Now we set up your site and your Google profile.',
    welcomeTime: 'Most people finish the next part in about 12 minutes.',
    welcomeGo: "Let's go",
    stepOf: (i: number, total: number) => `Step ${i} of ${total}`,
    saveAndLater: "Save and finish later. We'll keep your place.",
    stepTitle: {
      facts: 'Confirm your facts',
      google: 'Add us to your Google profile',
      domain: 'Connect your domain',
      brand: 'Check your site',
      photos: 'Add your photos',
      licence: 'Your license and insurance',
      go_live: 'Approve go-live',
    } as Record<string, string>,
    stepTime: { facts: 'About 4 minutes', google: 'About 3 minutes', domain: 'About 3 minutes', brand: 'About 2 minutes', photos: 'About 2 minutes', licence: 'About 1 minute', go_live: 'About 1 minute' } as Record<string, string>,
    state: { todo: 'To do', waiting_on_client: 'Waiting on you', waiting_on_us: "We're on it", done: 'Done', skipped: 'Skipped' } as Record<string, string>,
    factsLead: 'We filled this in from your Google profile and your current site. Tick what’s right and fix what isn’t.',
    factsTitle: 'Is this right?',
    group: { business: 'Business', services: 'Services', towns: 'Towns', hours: 'Hours', extras: 'Extras' } as Record<string, string>,
    right: "That's right",
    fix: 'Fix it',
    notSure: 'Not sure',
    ticked: 'Ticked',
    notSureMarked: "Marked not sure. We'll check it with you.",
    newValue: 'The right value',
    updatingPreview: 'Updating your preview',
    updatedPreview: 'Updated',
    factsDone: 'All done here',
    googleLead: 'Invite this address as a Manager on your Google profile. We accept within 4 business hours.',
    googleAddress: 'Our address to invite',
    googleSteps: ['Open Google Maps and sign in with the Google login you use for your business.', 'Tap your business, then Business Profile settings, then People and access.', 'Tap Add, paste our address and choose Manager. Tap Invite.'],
    googleSent: "I've sent it",
    googleThanks: "Thanks. We'll accept it within 4 business hours and let you know.",
    googleWhichLogin: 'Not sure which Google login you use?',
    googleWhichLoginBody: 'Look for the email Google sends when someone reviews you. That’s the login. Or ask us and we’ll find it together.',
    domainTitle: 'Where did you buy your domain?',
    domainLead: "Not sure? Type your domain and we'll look it up.",
    domainField: 'Your domain',
    domainHint: 'Like riveraplumbing.com',
    domainLookup: 'Look it up',
    registrar: { godaddy: 'GoDaddy', namecheap: 'Namecheap', squarespace: 'Squarespace', wix: 'Wix', cloudflare: 'Cloudflare', other: 'Something else', none: "I don't have one", web_person: 'My web person handles it' } as Record<string, string>,
    domainRecords: 'Add these two records',
    domainRecordsLead: 'Sign in where you bought your domain, open its DNS settings and add these. Copy each one exactly.',
    domainChecking: "We're checking your settings",
    domainCheckingLead: "This can take a few minutes after you save. You can close the app. We'll let you know when it's done.",
    domainFound: 'Found it',
    domainNotYet: 'Not yet',
    domainAllGood: "Your domain is connected. We'll take it from here.",
    domainCheckNow: 'Check again',
    recordName: 'Name (or Host)',
    recordValue: 'Value (or Points to)',
    webPersonEmail: "Your web person's email",
    webPersonSend: 'Send them the records',
    webPersonSent: (email: string) => `We sent the records to ${email}. We'll tell you when they're in.`,
    emailSafe: "Your email isn't touched. We only change where your website points.",
    brandTitle: 'Is your logo right? Are your colors right? Is anything wrong?',
    brandLead: "Tap anything on the page to leave a note. We'll fix it and show you again.",
    round: (c: number, m: number) => `Round ${c} of ${m}`,
    logoOk: 'Is your logo right?',
    colorsOk: 'Are your colors right?',
    anythingWrong: 'Anything else to fix?',
    notePlaceholder: 'What should change?',
    addNote: 'Add a note',
    pinAt: (i: number) => `Note ${i}`,
    tapToComment: 'Tap to comment',
    previewPhone: 'Phone',
    previewDesktop: 'Computer',
    previewNotReady: "Your preview is still building. It'll show here in a few minutes.",
    sendNotes: 'Send my notes',
    looksGood: 'It all looks good',
    photosLead: 'Real photos of your team, vans and work make your site yours. Add them in the app. Your site goes live once we have 3 of finished work, 1 of you or your team and 1 of your van or shop.',
    photosBrief: ['Your team, smiling, in uniform.', 'Your vans with the name showing.', 'Before and after shots of real jobs.', "Skip anything with a customer's face or house number."],
    photosDone: "I'm done for now",
    photosAllSent: "That's every photo your site needs. We're checking them now, so there's nothing more to do here.",
    photosApproved: 'Your photos are in. Thanks.',
    photosSeeAll: 'See all your photos',
    photosNotYet: 'Your site needs a few more photos before this step is done.',
    licenceLead: 'We found this on the state register. Is it right?',
    licenceWrong: "That's not right",
    insurance: 'Your insurance certificate',
    insuranceLead: 'A photo or PDF of the certificate, and when it runs out.',
    insuranceExpiry: 'It runs out on',
    insuranceUpload: 'Add the certificate',
    goLiveTitle: 'Your site is ready.',
    goLiveLead: "Here's what happens when you tap Go live:",
    goLiveBullets: (domain: string) => [`${domain} starts showing your new site within a few minutes.`, 'Your old pages send visitors to the matching new ones.', 'Google gets your new sitemap.'],
    goLiveEmail: "Your email isn't touched.",
    lookAgain: 'Look at it one more time',
    goLive: 'Go live',
    goLiveWho: (names: string) => `Only ${names} can approve go-live.`,
    remind: (name: string) => `Remind ${name}`,
    reminded: 'We sent them a reminder.',
    liveTitle: "You're live.",
    liveLead: (domain: string) => `${domain} is showing your new site. Next week we'll send your first "what we did" update.`,
    liveNext: 'Three things to do next',
    liveNextItems: ['Turn on lead alerts', 'Ask for your first review', 'Add a job'],
  },
  leads: {
    title: 'Leads',
    filters: 'Show',
    filter: { all: 'All', new: 'New', calls: 'Calls', forms: 'Forms', won: 'Won', filtered: 'Filtered' } as Record<string, string>,
    filteredNote: 'These looked like spam, so we kept them out of your inbox. Open one and tap Not spam to move it back.',
    filteredEmpty: 'Nothing filtered. Every form that came in is in your inbox.',
    anon: 'Someone tapped Call',
    status: { new: 'New', contacted: 'Contacted', booked: 'Booked', won: 'Won', lost: 'Lost', spam: 'Spam' } as Record<string, string>,
    source: { form: 'Form on your site', estimator: 'Price estimator', booking_click: 'Tapped Book', manual: 'Added by you', integration: 'From your job software' } as Record<string, string>,
    firstRun: 'Your leads will show up here the moment someone fills in a form on your site.',
    firstRunTitle: 'No leads yet',
    emptyFilter: 'Nothing here with this filter.',
    showAll: 'Show all leads',
    callTapsTitle: (count: number) => (count === 1 ? '1 person tapped Call on your site today' : `${count} people tapped Call on your site today`),
    callTapsOther: (count: number, d: string) => `${count} taps on Call ${d}`,
    callTapsInfo: ['Taps on Call', "A tap means someone pressed your number. We can't see whether the call connected.", 'Where it’s from: your website.'],
    fromPage: (label: string) => `From your ${label}`,
    export: 'Download as a spreadsheet',
    callBack: 'Call back',
    text: 'Text',
    email: 'Email',
    map: 'Map',
    cameIn: (time: string, page: string | null) => (page ? `Came in ${time} from your ${page}.` : `Came in ${time}.`),
    noMessage: 'They didn’t leave a message.',
    delivery: 'How it reached you',
    deliveryInfo: ['How it reached you', 'Every way we told you about this lead, and when. If a phone missed it, email still had it.', 'Where it’s from: our delivery records.'],
    deliveryRow: (channel: string, who: string, status: string, time: string) => `${channel} to ${who}: ${status} ${time}`,
    channel: { push: 'Alert', email: 'Email', sms: 'Text' } as Record<string, string>,
    deliveryStatus: { queued: 'waiting', sent: 'sent', delivered: 'delivered', opened: 'opened', clicked: 'opened', failed: "didn't arrive", suppressed: 'not sent' } as Record<string, string>,
    statusLabel: 'Where this lead is',
    statusSaved: (s: string) => `Marked ${s}.`,
    undo: 'Undo',
    notSpam: 'Not spam',
    jobDone: 'Job done? Ask for a review',
    jobDoneLead: "We'll fill in their name and email. You check it and send.",
    purge: (d: string) => `We keep their details only while you're with us. If you ever leave, they're deleted 90 days later. Held until at least ${d}.`,
    noPermission: "Your role doesn't include leads. The owner or a manager can see them.",
  },
  reviews: {
    title: 'Reviews',
    ask: 'Ask for a review',
    paceTitle: 'Your pace',
    pace: (mine: number, top: number | null) => (top === null ? `You got ${n(mine, 'new review', 'new reviews')} this week.` : `You got ${n(mine, 'new review', 'new reviews')} this week. Businesses in your top three average ${top}.`),
    paceInfo: ['Your pace', 'New Google reviews in the last 7 days, next to the average for the three businesses above you on your map.', 'It’s a guide, not a target. Steady beats a burst.'],
    filter: { all: 'All', needs_you: 'Needs you', low: '1 to 3 stars' } as Record<string, string>,
    filters: 'Show',
    stars: (r: number) => `${r} star${r === 1 ? '' : 's'}`,
    noComment: 'No written review. Just the stars.',
    reply: { none: 'No reply yet', drafted: 'Reply drafted', awaiting_client: 'Needs your look', approved: 'Reply on its way', posted: 'Replied', not_replying: 'Not replying' } as Record<string, string>,
    firstRunTitle: 'No reviews yet',
    firstRun: 'Your Google reviews show up here within an hour of being posted. Ask a happy customer to get the first one.',
    empty: 'No reviews with this filter.',
    sent: 'Requests you sent',
    qr: 'Your review card',
    yourReply: 'Your reply',
    ourDraft: 'Our draft reply',
    draftBy: { us: 'We drafted this in your voice.', you: 'You wrote this.' } as Record<string, string>,
    postIt: 'Post it',
    changeIt: 'Change it',
    callMe: 'Call me about this',
    notReplying: "Don't reply",
    posted: "Posted. It'll show on Google within the hour.",
    queued: "Approved. We'll post it within the hour.",
    callBooked: "We'll call you today to talk it through.",
    seeOnGoogle: 'See it on Google',
    sensitiveNote: 'A calm reply helps more than a fast one. Nothing posts until you say so.',
    editLabel: 'Your reply',
    saveReply: 'Save and post',
    /** Deck rulings c022, c072, c100: review requests go by email only. */
    emailOnly: 'Review requests go out by email.',
    askTitle: 'Ask for a review',
    firstName: 'First name (optional)',
    contact: 'Their email',
    contactHint: 'We send one email in your words and one reminder.',
    contactError: 'Enter their email, like name@example.com',
    attest: 'This customer gave us their email for this job and knows we may email them about it.',
    attestError: 'Tick this to send. We only email people who gave you their email for a job.',
    /** Shown in place of the tick once this person has confirmed it (docs/52 S42). */
    attestKept: "You've confirmed you only ask customers who gave you their email for a job.",
    attestMany: 'These customers gave us their email for a job and know we may email them about it.',
    when: "We'll send one email between 10 am and 7 pm their time, and one reminder in 3 days if they haven't opened it.",
    /** Deck ruling c131: the review ask emails, in the owner's words. */
    templates: {
      link: 'Your review emails',
      title: 'Your review emails',
      lead: "These are the emails your customers get when you ask for a review. We wrote them for your business. Change the words any time.",
      request: 'The first email',
      reminder: 'The reminder',
      subject: 'Subject',
      body: 'Message',
      placeholders: "Type {first_name}, {business} or {owner} and we'll fill them in for each customer.",
      fixed: 'We add the review button, your business address and an unsubscribe link to every email.',
      save: 'Save',
      reset: 'Go back to ours',
      saved: 'Saved. Your next review asks use it.',
      resetDone: 'Back to the email we wrote for you.',
      preview: 'How it reads',
      edited: 'Your words',
      ours: 'Written for you',
      onlyOwner: 'Only the owner can change the review emails.',
      emptyError: 'Write a few words first.',
    },
    send: 'Send',
    scheduled: (time: string) => `Scheduled for ${time}.`,
    scheduledNoTime: 'Scheduled. It goes out between 10 am and 7 pm their time.',
    alreadyAsked: 'Already asked in the last 12 months. Nothing was sent.',
    askSuppressed: 'They asked not to be emailed. Nothing was sent.',
    cancelled: 'Cancelled. Nothing was sent.',
    sentTitle: 'Requests you sent',
    sentEmpty: 'No requests yet. Ask your next happy customer.',
    rrStatus: { held: 'Held', scheduled: 'Scheduled', sent: 'Sent', opened: 'Opened', clicked: 'Tapped the link', failed: "Didn't send", cancelled: 'Cancelled', suppressed: 'Opted out', duplicate: 'Already asked' } as Record<string, string>,
    purged: 'Contact details deleted after 90 days',
    purgedTitle: 'Details deleted',
    optedOut: 'Opted out. We won’t contact them again.',
    reminderAt: (t: string) => `Reminder ${t}`,
    cancel: 'Cancel',
    bulk: 'Ask a whole list',
    crewNote: 'You can ask for reviews for your own jobs.',
    /** c133: the tracked review link, handed to the owner's own phone to send however they like. */
    share: 'Share your review link',
    shareFailed: "We couldn't open sharing on this phone. Copy the link on your review card.",
    linkNotReady: "We're still connecting your Google review link. Sharing shows up here when it's ready.",
    moreWays: {
      title: 'More ways to ask',
      share: 'Share your link',
      shareMeta: 'Send your review link yourself, any way you like.',
      card: 'Your review card',
      cardMeta: 'A card to print. Customers scan it with their phone camera.',
      paste: 'Paste several emails',
      pasteMeta: (max: number) => `Up to ${max} at a time.`,
      csv: "Import today's jobs",
      csvMeta: "A CSV file of today's jobs from your job software.",
    },
    /** c133: several asks at once. A paste, or a CSV of today's jobs. Never a list of past customers (c132). */
    several: {
      title: 'Ask several customers',
      mode: 'How to add them',
      modes: { paste: 'Paste emails', csv: "Today's jobs" } as Record<string, string>,
      pasteLead: (max: number) => `Paste up to ${max} emails. We check each one before anything sends.`,
      pasteLabel: 'Paste emails',
      pasteHint: 'One per line, or separated by commas. A name before the email is fine.',
      pasteEmpty: "We couldn't find an email in that. Put one on each line, like maria@example.com",
      check: 'Check',
      csvLead: "This is for today's jobs only. Export today's jobs from your job software as a CSV file and choose it here. Older jobs are skipped.",
      csvPrivacy: "We read the file on your phone and it's never uploaded. We only check the name, email and job date on each row.",
      csvPick: 'Choose a CSV file',
      csvAgain: 'Choose another file',
      csvRead: (name: string, rows: number) => `${name}: ${n(rows, 'row', 'rows')} read.`,
      csvNoEmail: 'That file needs an email column. Add one and choose the file again.',
      csvNoDate: "That file needs a job date column so we can pick out today's jobs.",
      csvEmpty: "We couldn't find a row with an email in that file.",
      csvUnreadable: "We couldn't read that file. Save it as a CSV file and try again.",
      csvTooBig: "That file is too big for one day's jobs. Export today's jobs only and try again.",
      results: 'What we found',
      willAsk: (count: number) => `${n(count, 'customer', 'customers')} will be asked.`,
      unread: (count: number) => `${n(count, 'line', 'lines')} had no email and ${count === 1 ? 'was' : 'were'} left out.`,
      dropped: (count: number, max: number) => `We checked the first ${max}. ${n(count, 'more row', 'more rows')} ${count === 1 ? 'was' : 'were'} left out.`,
      leftToday: (count: number) => `You can ask ${n(count, 'more customer', 'more customers')} today.`,
      okChip: 'Will be asked',
      skipChip: 'Skipped',
      send: (count: number) => `Send ${n(count, 'request', 'requests')}`,
      done: (count: number) => `${n(count, 'request', 'requests')} scheduled.`,
      doneNone: 'Nothing was sent.',
      firstSend: (time: string) => `The first one goes out at ${time}.`,
      skipped: 'Not asked',
      seeSent: 'See requests you sent',
      again: 'Ask more customers',
    },
    /** c133: the printable card and the taps on the tracked link. */
    card: {
      lead: 'Print it for the van dash or leave it with the invoice. Customers scan it and land on your Google review form.',
      preview: 'Your card',
      qrLabel: (business: string) => `QR code that opens the Google review form for ${business}`,
      linkLabel: 'Your review link',
      print: 'Print your card',
      printHint: 'Each one downloads as a PDF file, ready for a print shop or your own printer.',
      name: { dash: 'Van dash card', leave_behind: 'Leave behind card' } as Record<string, string>,
      download: (name: string, width: number, height: number) => `${name}, ${width} by ${height} inches`,
      downloadFailed: "We couldn't get your card. Check your connection and try again.",
      taps: 'Taps on your link',
      tapsInfo: ['Taps on your link', 'Each time someone opens your review link: scanned on your card, shared by you or tapped in a review email.', "A tap isn't a review. It tells you the ask reached them."],
      last7: 'Last 7 days',
      last30: 'Last 30 days',
      tapsLine: (period: string, card: number, link: number, emails: number) => `${period}: ${card} from your card, ${link} from your link and ${emails} from review emails.`,
      asks7: (count: number) => `You sent ${n(count, 'review request', 'review requests')} in the last 7 days.`,
      latest: (when: string) => `Latest tap: ${when}.`,
      notReadyTitle: "Your review link isn't ready yet",
      notReady: "We're still connecting your Google review link. Your card and your review requests show up here when it's ready.",
      walkUps: (count: number) => `${n(count, 'tap', 'taps')} on your card and shared link in the last 7 days.`,
    },
  },
  jobs: {
    title: 'Jobs',
    add: 'Add a job',
    addLead: "Add a few photos and tell us what you did. We'll turn it into a post for your site and your Google profile.",
    takePhoto: 'Take a photo',
    choosePhotos: 'Choose photos',
    photos: 'Photos',
    photoCount: (count: number) => n(count, 'photo', 'photos'),
    whatDid: 'What did you do?',
    whatDidHint: 'A line is plenty. Like "50 gallon water heater, same day".',
    town: 'Town',
    townGuess: (t: string) => `From the photo's location we think this was in ${t}. The location itself stays on your phone.`,
    service: 'Service',
    pick: 'Pick one',
    send: 'Send this job',
    needSomething: 'Add a photo or say what you did.',
    uploading: (done: number, total: number) => `${done} of ${total} photos uploaded`,
    uploadFailed: "This photo didn't upload.",
    retry: 'Try again',
    removePhoto: (i: number) => `Remove photo ${i}`,
    moveUp: (i: number) => `Move photo ${i} earlier`,
    moveDown: (i: number) => `Move photo ${i} later`,
    photoN: (i: number, total: number) => `Photo ${i} of ${total}`,
    tooBig: (name: string) => `${name} is too big. Photos can be up to 20 MB.`,
    tooMany: 'That’s the most for one job: 50 photos.',
    notPhoto: (name: string) => `${name} isn't a photo we can take.`,
    privacy: "We remove the location from every photo before it leaves your phone.",
    gotIt: 'Got it.',
    gotItLead: (d: string) => `We'll have your post ready by ${d}. You'll see it here when it's live.`,
    queuedTitle: 'Saved on your phone',
    queuedLead: "You're offline. We'll send your job and photos when you're back online.",
    firstRunTitle: 'No jobs yet',
    firstRun: 'Add a job with a few photos after your next call. We turn each one into a page on your site and a post on your Google profile.',
    status: { received: 'Received', drafting: "We're writing it", in_review: 'Being checked', awaiting_client: 'Needs your OK', published: 'Live', rejected: "Couldn't use" } as Record<string, string>,
    whereWent: 'Where it went',
    output: { site_project: 'Your website', gbp_post: 'Google post', gbp_photo: 'Google photo', fb_post: 'Facebook', ig_post: 'Instagram', review_reply: 'Review reply' } as Record<string, string>,
    outputState: { drafted: 'Being written', in_review: 'Being checked', approved: 'Approved', queued: 'Going out soon', live: 'Live', removed: 'Removed', rejected: 'Not used' } as Record<string, string>,
    noOutputs: "We're writing your post now. It shows here when it's ready.",
    yourWords: 'Your words',
    dontUse: "Don't use this photo",
    useAgain: 'Use this photo',
    excluded: "We won't use this one",
    library: 'All your photos',
    libraryLead: 'Every photo you sent us and where it’s used.',
    libraryEmpty: 'No photos yet. Photos from your jobs show up here.',
    usedIn: (count: number) => (count ? `Used in ${n(count, 'place', 'places')}` : 'Not used yet'),
    flagged: 'Flagged',
    addedBy: (name: string) => `Added by ${name}`,
    readyBy: (d: string) => `Ready by ${d}`,
  },
  /** The photo library and the photos the site needs before it goes live (deck rulings c057, k11). */
  photos: {
    title: 'Your photos',
    lead: 'Every photo you sent us, what it shows and where it sits on your site.',
    add: 'Add photos',
    empty: 'No photos yet',
    emptyLead: 'Add photos of your finished work, your team and your van. We check each one and put the best on your site.',
    kindLabel: 'What do these photos show?',
    kindHint: 'Pick one so each photo lands in the right place on your site.',
    kind: {
      work: 'Finished work',
      before: 'Before the job',
      after: 'After the job',
      people: 'You or your team',
      premises: 'Your shop or yard',
      vehicle: 'Your van or truck',
      logo: 'Your logo',
    } as Record<string, string>,
    kindUnset: 'Tell us what this shows',
    kindChange: 'What this shows',
    serviceLabel: 'Which service is it?',
    serviceNone: 'No one service',
    /** What the site still needs, by need. Joined with `list()`: "2 more photos of finished work and 1 photo of your team". */
    need: {
      work: (count: number) => (count === 1 ? '1 more photo of finished work' : `${count} more photos of finished work`),
      people: (count: number) => (count === 1 ? '1 photo of you or your team' : `${count} photos of you or your team`),
      place: (count: number) => (count === 1 ? '1 photo of your van or shop' : `${count} photos of your van or shop`),
    },
    stillNeeded: (parts: readonly string[]) => `Your site still needs ${list(parts)}.`,
    allSent: "That's every photo your site needs. We're checking them now.",
    allApproved: 'Your site has the photos it needs. Keep adding them as you finish jobs.',
    progress: (sent: number, needed: number) => `${sent} of ${needed} photos sent`,
    state: {
      checking: 'Checking',
      waiting: 'With us for a look',
      approved: 'Ready for your site',
      onSite: 'On your site',
      setAside: 'Not in use',
      cantUse: "Can't use",
    } as Record<string, string>,
    checkingNote: "We're checking this one. It takes about a minute.",
    waitingNote: "It passed our checks. We'll look at it and place it on your site.",
    /** One sentence per reason a photo can't be used. Each says what to do next. */
    problem: {
      not_a_photo: "This file isn't a photo we can take. Send a JPEG, PNG or HEIC.",
      unreadable: "We couldn't open this one. Try sending it again.",
      too_large: 'This photo is too large for us to open. Send a smaller copy.',
      too_small: 'This one is too small to look sharp on your site. Send the original off your phone.',
      too_dark: 'This one is too dark to see. Try again with the lights on or in daylight.',
      blurry: 'This one is blurry. Hold steady, tap to focus and send another.',
      duplicate: "You've already sent us this one.",
      private: "This one shows a customer's face, house number or car plate, so we can't use it.",
      not_yours: 'We can only use photos of your own work, team and place.',
      other: "We can't use this one on your site.",
    } as Record<string, string>,
    favourite: 'Use this one first',
    unfavourite: 'Stop using this one first',
    isFavourite: 'A favorite',
    rename: 'Name this photo',
    nameLabel: 'Name',
    nameHint: 'Only you see this. It helps you find the photo later.',
    save: 'Save',
    remove: 'Remove this photo',
    removeTitle: 'Remove this photo?',
    removeLead: "We'll delete it from your photos for good.",
    removeOnSite: "It's on your site now. It comes off within a few minutes.",
    removeConfirm: 'Remove it',
    removed: 'Photo removed',
    keep: 'Keep it',
    dontUse: "Don't use this one",
    useAgain: 'Use this one again',
    /** Where a photo sits on their site, in their words. A service shows by its own name. */
    slot: { hero: 'The top of your home page', about: 'Your about section', cta: 'The call band', financing: 'Your financing section', coverage: 'Your service area section', other: 'Your site' } as Record<string, string>,
    placedIn: (places: readonly string[]) => `On your site: ${list(places)}`,
    notPlaced: 'Not on your site yet',
    filter: { all: 'All', favourites: 'Favorites', flagged: "Can't use", used: 'On your site' } as Record<string, string>,
    uploading: (done: number, total: number) => `${done} of ${total} uploaded`,
    waitingOffline: "You're offline. These upload when you're back online.",
    uploadFailed: "This photo didn't upload.",
    retryAll: 'Try again',
    sent: (count: number) => (count === 1 ? '1 photo sent. We check it next.' : `${count} photos sent. We check them next.`),
    photoOf: (i: number, total: number, name: string) => (name ? `${name}, photo ${i} of ${total}` : `Photo ${i} of ${total}`),
    more: 'Load more photos',
  },
  inbox: {
    title: 'Inbox',
    newRequest: 'New request',
    waitingOnYou: 'Waiting on you',
    inProgress: 'In progress',
    done: 'Done',
    by: (d: string) => `By ${d}`,
    kind: { request: 'Request', approval: 'Needs your OK', decision: 'Decision', message: 'Message', report: 'Report' } as Record<string, string>,
    firstRunTitle: 'Nothing here yet',
    firstRun: "Ask us for anything: a change to your site, your hours, a question about your bill. It all lives here with a date we'll have it done.",
    nothingWaiting: 'Nothing is waiting on you.',
    newTitle: 'What do you need?',
    newLead: 'Tell us in your own words. You can type or add a photo.',
    quick: { change_hours: 'Change my hours', add_service: 'Add a service', fix_site: 'Fix something on my site', billing_question: 'A question about my bill', something_else: 'Something else' } as Record<string, string>,
    quickText: { change_hours: 'Our hours are changing: ', add_service: "We'd like to add a service: ", fix_site: 'Something on my site needs fixing: ', billing_question: 'A question about my bill: ', something_else: 'Here’s what I need: ' } as Record<string, string>,
    bodyLabel: 'Your request',
    bodyError: 'Tell us what you need in a few words.',
    attach: 'Add a photo',
    attached: (count: number) => n(count, 'photo attached', 'photos attached'),
    send: 'Send',
    thanksTitle: "Thanks. We're on it.",
    thanksLead: (d: string) => `You'll hear from us by ${d}.`,
    queued: "You're offline. We'll send your request when you're back online.",
    stage: { received: 'Received', working: 'Working on it', ready_for_ok: 'Ready for your OK', live: 'Live', closed: 'Closed' } as Record<string, string>,
    number: (num: number) => `Request ${num}`,
    openRequest: 'See your request',
    promised: (d: string) => `We'll have this done by ${d}.`,
    progress: 'Progress',
    thread: 'Messages',
    you: 'You',
    reply: 'Reply',
    replyLabel: 'Write a reply',
    replySent: 'Sent.',
    readyTitle: 'Ready for your OK',
    readyLead: "Here's your site with the change. Nothing's live until you approve it.",
    /** Deck ruling c140: "show me first" waits 7 days, with reminders on day 2 and day 5. */
    readyWait: "We'll hold it for 7 days and remind you on day 2 and day 5. If we don't hear back, we close it and nothing changes.",
    changes: 'What changes',
    was: (v: string) => `was ${v.toLowerCase()}`,
    none: 'nothing',
    google: 'Also changes',
    before: 'Before',
    after: 'After',
    beforeAfter: 'Show before or after',
    shotAlt: (which: string) => `${which} the change, on a phone`,
    approve: 'Approve',
    changeSomething: 'Change something',
    changeLabel: 'What should be different?',
    changeSend: 'Send',
    approved: "Approved. It'll be live within the hour.",
    liveTitle: "It's live.",
    liveLead: 'Your change is on your website and your Google profile.',
    seeIt: 'See it',
    putBack: 'Put it back',
    putBackConfirm: 'Put it back the way it was? Our team checks it and it goes back within the day.',
    putBackDone: "We're putting it back. You'll see it here when it's done.",
    putBackUntil: (d: string) => `You can put this back until ${d}.`,
    rateTitle: 'Is this sorted?',
    sorted: 'Sorted',
    notYet: 'Not yet',
    rated: 'Thanks for telling us.',
    notApprover: (who: string) => `Only ${who} can approve this.`,
    remindOwner: (who: string) => `Remind ${who}`,
    reminded: 'We sent them a reminder.',
    decisionWhat: "What we'd change",
    decisionWhy: 'Why',
    decisionRisks: 'The risks',
    decisionDo: "What we'll do",
    typeName: 'Type your name to approve',
    typeNameHint: 'This is your signature for this one change.',
    typeNameError: 'Type your name to approve.',
    notNow: 'Not now',
    talkFirst: 'Talk to me first',
    decisionApproved: "Approved. We'll make only this change and watch it closely.",
    decisionDeclined: "OK. We won't change it.",
    decisionTalk: "We'll call you before anything changes.",
    decisionClosed: { approved: 'You approved this.', declined: 'You said not now.', talk_first: "You asked us to talk first. We'll call you.", executed: 'Done. We made this change.', expired: 'This expired.' } as Record<string, string>,
  },
  notifications: {
    title: 'Notifications',
    markAll: 'Mark all read',
    empty: "You're all caught up. New leads, reviews and anything we need from you show up here.",
    today: 'Today',
    earlier: 'Earlier',
    settings: 'Notification settings',
  },
  hours: {
    title: 'Your hours',
    lead: "These show on your website and your Google profile. Change them here and we'll update both.",
    open: 'Open',
    closed: 'Closed',
    allDay: 'Open 24 hours',
    from: 'Opens',
    to: 'Closes',
    endAfterStart: 'Closing time must be after opening time.',
    day: { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' } as Record<string, string>,
    closures: 'Days you’re closed',
    addClosure: 'Add a closure',
    closureFrom: 'From',
    closureTo: 'To',
    closureLabel: 'Reason (optional)',
    summary: 'Here’s what changes',
    noChanges: 'Nothing has changed yet.',
    changeLine: (d: string, after: string, before: string) => `${d}: ${after}, was ${before}`,
    send: 'Send the change',
    sameDay: 'Sent before 3 pm Eastern, it goes live today.',
    sent: (d: string) => `Sent. Your new hours go live ${d}.`,
    pending: 'You already have an hours change on its way.',
  },
  banner: {
    title: 'Show a banner on your site',
    lead: "Pick one. It'll show on every page within the hour, after a quick check by our team.",
    until: 'Show it until',
    send: 'Show this banner',
    sent: "We're checking it now. It'll be up within the hour.",
    live: 'On your site now',
  },
  reports: {
    title: 'Reports',
    lead: 'A report every month: your map, your calls, your reviews and what we did.',
    first: (d: string) => `Your first report lands on ${d}.`,
    next: (d: string) => `Your next report lands on ${d}.`,
    open: 'Read it',
    headline: 'The short version',
    calls: 'Calls and clicks',
    reviews: 'Reviews',
    newReviews: (count: number) => n(count, 'new review', 'new reviews'),
    average: (a: string) => `Average ${a} stars`,
    whatWeDid: 'What we did',
    yourSide: 'Your side',
    whatsNext: "What's next",
    notes: 'Notes',
    download: 'Download PDF',
    share: 'Share a read only link',
    shared: 'Link copied. It works for 14 days.',
    mapTitle: 'Your map',
    mapLead: 'Where you show in Google’s top three around your shop, month by month.',
    mapMonth: 'Month',
    mapEarlier: 'Earlier month',
    mapLater: 'Later month',
    mapOf: (top: number) => `Top three on ${top} of 81 points`,
    /** Deck rulings c136, c137: each report covers the time since the scan before, and says how the map moved. */
    covers: (label: string) => `Covers ${label}`,
    movementTitle: 'Your map since day one',
    movement: { up: 'Up', held: 'Held', down: 'Down' } as Record<string, string>,
    movementLine: {
      up: 'Your map moved up since day one.',
      held: 'Your map held steady since day one.',
      down: "Your map dipped since day one. We'll show you why and what's next.",
    } as Record<string, string>,
    movementInfo: 'Held means within the spread of your two day one scans, or within 2 of the 81 points, whichever is larger. Small moves inside that are normal day to day noise.',
    mapBaseline: 'Day one',
    legend: { top3: '1 to 3', near: '4 to 10', far: '11 to 20', none: 'Not in the top 20' } as Record<string, string>,
    mapTable: 'Every point as a table',
    mapTableCaption: (keyword: string, d: string) => `Rank at each of 81 points for "${keyword}", ${d}. North is the first row.`,
    notFound: 'Not in the top 20',
    ledger: 'What we did that month',
    ledgerNote: 'Listed by date. We don’t claim any one thing moved the map.',
    day90: 'Your day 90 check',
    day90Soon: (d: string) => `Your day 90 check is on ${d}. We'll show the working here.`,
    day90Base: (v: string) => `Day one: in the top three on ${v} of 81 points (the average of two scans).`,
    rank: (r: number | null) => (r === null ? 'Not in the top 20' : `Rank ${r}`),
    point: (row: number, col: number) => `Row ${row}, column ${col}`,
    /** The monthly proof report (r26): the headline number, the supporting numbers and one map per service. */
    numbers: 'The numbers',
    numbersInfo: 'Each number shows this cycle and the cycle before. When the earlier number is under 10 we show the count and not a percentage.',
    groups: { website: 'Your website', google: 'Your Google profile', reviews: 'Reviews' } as Record<string, string>,
    thisCycle: 'This cycle',
    lastCycle: (v: string) => `Last cycle: ${v}`,
    notMeasured: 'Not measured this cycle',
    noEarlier: 'No earlier cycle to compare',
    noChange: 'No change',
    changeMore: (count: number) => `${count} more`,
    changeFewer: (count: number) => `${count} fewer`,
    changeUp: (count: number, percent: number) => `${count} more, up ${percent}%`,
    changeDown: (count: number, percent: number) => `${count} fewer, down ${percent}%`,
    topThreeOn: (v: string) => `In the top three on ${v} of 81 points`,
    dayOneWas: (v: string) => `Day one: ${v} of 81`,
    overall: (v: string) => `Across your three services you're in the top three on ${v} of 81 points.`,
    overallDayOne: (v: string) => `On day one it was ${v}.`,
    didCounts: 'What we did, counted',
    /** The map screen (S81): day one, the latest scan and the change, for each service. */
    service: 'Service',
    view: 'Which map',
    views: { baseline: 'Day one', latest: 'Latest', change: 'Change' } as Record<string, string>,
    scannedOn: (d: string) => `Scanned ${d}`,
    pairNote: 'This number is the average of two scans. The map shows one of them.',
    gridFacts: (spacing: string, d: string) => `81 points, ${spacing} apart, set on ${d} and never changed.`,
    nextScan: (d: string) => `Your next scan is on ${d}.`,
    mapEmptyTitle: 'No map yet',
    mapEmptyBody: 'Your first map comes with your day one scan.',
    latestWaiting: 'Your latest map shows here after your next scan.',
    changeWaiting: 'The change shows here after your next scan.',
    changeUnreadable: "We can't compare these two maps point by point. The numbers above still stand.",
    changeLegend: {
      gained: 'In the top three now, not on day one',
      lost: 'In the top three on day one, not now',
      kept: 'In the top three both times',
      same: 'Outside the top three both times',
    } as Record<string, string>,
    changeCell: { gained: 'Gained', lost: 'Lost', kept: 'Kept', same: 'No change' } as Record<string, string>,
    changeMoreThan: (count: number) => `In the top three at ${n(count, 'more point', 'more points')} than day one`,
    changeFewerThan: (count: number) => `In the top three at ${n(count, 'fewer point', 'fewer points')} than day one`,
    changeSameAs: 'In the top three at the same number of points as day one',
    changeCounts: (gained: number, lost: number, kept: number) => `${gained} gained, ${lost} lost and ${kept} kept`,
    changeTable: 'The change as a table',
    changeTableCaption: (keyword: string) => `Change since day one at each of 81 points for "${keyword}". North is the first row.`,
    everyScan: 'Every scan',
    /** The fact that holds the three searches we measure (measure.money-services). */
    moneyServices: 'Your three money services',
  },
  /** How a fact's value reads on the facts screens (apps/app/src/web/lib/facts.ts). Never JSON, never a raw key. */
  facts: {
    yes: 'Yes',
    no: 'No',
    offered: 'Offered',
    notOffered: 'Not offered',
    notSet: 'Not set yet',
    closed: 'Closed',
    miles: (n: string) => (n === '1' ? '1 mile' : `${n} miles`),
    stars: (rating: string, count: string, platform: string | null) => `${rating} stars from ${count} reviews${platform ? ` on ${platform}` : ''}`,
    checked: (date: string, from: string) => `Checked ${date} ${from}`,
    licenseType: 'License type',
    number: 'Number',
    issuedBy: 'Issued by',
    deal: 'Deal',
    inShort: 'In short',
    terms: 'Terms',
    ends: 'Ends',
    lender: 'Lender',
    finePrint: 'Fine print',
    name: 'Name',
    role: 'Role',
    since: 'Since',
    about: 'About',
    structuredEdit: "Tell us what's changed in your own words and our team will update it.",
    whatChanged: "What's changed",
  },
  account: {
    title: 'Account',
    signedInAs: (email: string) => `Signed in as ${email}`,
    role: { owner: 'Owner', manager: 'Manager', crew: 'Crew', bookkeeper: 'Bookkeeper' } as Record<string, string>,
    rows: {
      business: 'Business details',
      ownership: "What's yours",
      team: 'Team',
      billing: 'Billing',
      notifications: 'Notifications',
      security: 'Sign in and devices',
      appearance: 'Appearance',
      export: 'Download your data',
      reports: 'Reports',
      hours: 'Your hours',
      banner: 'Site banner',
      reviewEmails: 'Review emails',
      help: 'Help',
      signOut: 'Sign out',
    },
    rowsHint: {
      business: 'Name, phone, services and towns',
      ownership: 'Your domain, profile, site and data',
      team: 'People and what they can do',
      billing: 'Plan, card and invoices',
      notifications: 'What we tell you and how',
      security: 'Face ID, devices, email',
      appearance: 'Light, dark or match your phone',
      export: 'All your data in one file',
      reviewEmails: 'What customers get when you ask',
    } as Record<string, string>,
    signOutConfirm: 'Sign out on this phone? Your saved copy of your data is removed from it.',
    paymentProblem: 'Payment problem',
    appVersion: (v: string) => `Version ${v}`,
    business: 'Business details',
    businessLead: 'What your site and Google profile say about you. To change anything, send us a request.',
    askChange: 'Ask us to change this',
    appearanceTitle: 'Appearance',
    theme: { system: 'Match my phone', light: 'Light', dark: 'Dark' } as Record<string, string>,
    themeLead: 'Light is easiest to read in bright sun.',
  },
  ownership: {
    title: "What's yours",
    domain: (d: string, reg: string | null) => (reg ? `Your domain: ${d} is registered to you at ${reg}. We never hold it.` : `Your domain: ${d} is registered to you. We never hold it.`),
    noDomain: "Your domain: you don't have one connected yet.",
    profile: "Your Google profile: you're the owner. We're a manager you can remove at any time.",
    profileUnknown: "Your Google profile: it's yours. We're a manager you can remove at any time.",
    /** Deck ruling c013: the site is licensed while they're a client. Taking it away is a paid buyout, quoted on request. */
    site: "Your website: it's licensed to you while you're with us. If you'd like to keep it when you leave, ask us for a buyout quote.",
    data: 'Your data: your leads, customer contacts, reviews and reports are yours to download any time.',
    customers: "Your customers' details: we keep them only to send review requests and delete them after 90 days.",
    download: 'Download your data',
    buyout: 'Ask for a buyout quote',
    buyoutText: "I'd like a quote to keep my website if I leave. ",
  },
  team: {
    title: 'Team',
    lead: 'Everyone signs in as themselves with their own email.',
    you: 'You',
    invite: 'Invite someone',
    inviteTitle: 'Invite someone',
    email: 'Their email',
    role: 'What they can do',
    roleDetail: {
      manager: 'Leads, reviews, jobs, requests and hours. No billing.',
      crew: 'Add jobs and photos, and ask for reviews for their own jobs.',
      bookkeeper: 'Invoices and the card on file. Nothing else.',
      owner: 'Everything, including billing and canceling.',
    } as Record<string, string>,
    canApprove: 'Can approve changes',
    canApproveDetail: 'They can say yes to site changes and go-live for you.',
    send: 'Send invite',
    sent: (email: string) => `We emailed ${email} an invite. It works for 7 days.`,
    pending: 'Invites waiting',
    expires: (d: string) => `Invite works until ${d}`,
    withdraw: 'Withdraw',
    withdrawn: 'Invite withdrawn.',
    lastActive: (when: string) => `Last active ${when}`,
    neverActive: "Hasn't signed in yet",
    edit: (name: string) => `Change ${name}`,
    removePerson: (name: string) => `Remove ${name}`,
    removeConfirm: (name: string) => `Remove ${name}? They're signed out everywhere right away. Anything they added stays.`,
    removed: (name: string) => `${name} was removed.`,
    saved: 'Saved.',
    managerCanInviteCrew: 'Managers can invite crew. The owner can invite anyone.',
    onlyOwner: 'Only the owner can change roles.',
  },
  billing: {
    title: 'Billing',
    plan: 'Your plan',
    planLine: (label: string, amount: string) => `${label} · ${amount} a month`,
    nextCharge: 'Next charge',
    nextChargeLine: (amount: string, d: string) => `${amount} on ${d}`,
    minTerm: 'Minimum term ends',
    card: 'Card',
    cardLine: (brand: string, last4: string) => `${brand} ending ${last4}`,
    noCard: 'No card on file',
    updateCard: 'Update card',
    invoices: 'Invoices',
    invoicesEmpty: 'No invoices yet.',
    invoiceStatus: { draft: 'Draft', open: 'Due', paid: 'Paid', uncollectible: 'Not paid', void: 'Void' } as Record<string, string>,
    credits: 'Credits',
    cancel: 'Cancel my plan',
    status: { setup_paid: 'Setup paid', card_saved: 'Card saved', active: 'Active', past_due: 'Payment failed', work_paused: 'New work paused', canceling: 'Ending', ended: 'Ended', refunded: 'Refunded' } as Record<string, string>,
    opening: 'Opening Stripe',
    stripeNote: 'Card changes happen on Stripe, our payment company. You come straight back here after.',
    noPermission: 'Only the owner and the bookkeeper can see billing.',
    cancelTitle: "Before you go, here's exactly what happens",
    lastDay: (d: string, basis: string) => `Your last day of service: ${d} (${basis}).`,
    basis: { minimum_term: 'the end of your 90 day minimum', paid_period: 'the end of the month you paid for' } as Record<string, string>,
    untilThen: 'Until then everything keeps running.',
    afterThat: (d: string) => `After that your site stays up until ${d}, which gives you time to point your domain somewhere new. Your Google profile stays yours and we remove ourselves as a manager.`,
    downloadUntil: (d: string) => `You can download your data now or any time before ${d}.`,
    continue: 'Continue',
    saveTitle: 'Want a pause instead?',
    saveBody: "He'll go through your numbers with you. No pressure either way.",
    book: 'Or book 15 minutes with us first',
    cancelPlan: 'Cancel my plan',
    reason: "What's the main reason? (optional)",
    reasons: { too_expensive: 'Too expensive', no_results: 'Not seeing results', closing_or_selling: 'Closing or selling', other: 'Something else' } as Record<string, string>,
    canceledTitle: 'Your plan is canceled.',
    canceledLead: (d: string) => `Your last day is ${d}. We've emailed you the details.`,
    onlyOwner: 'Only the owner can cancel.',
    /** Deck ruling c031: the save offer is a pause. */
    pauseLead: "Pause for up to 30 days. We stop billing you and your site stays live. We pause new work and reports until you're back.",
    pauseOnce: 'You can pause once in any 12 months.',
    pauseLength: 'How long?',
    pauseDays: { 7: '1 week', 14: '2 weeks', 30: '30 days' } as Record<number, string>,
    pausePlan: 'Pause my plan',
    pauseUsed: (d: string) => `You've used your pause for this year. Your next one can start on ${d}.`,
    pauseRunning: 'Your plan is already paused.',
    pausedTitle: 'Your plan is paused.',
    pausedLead: (d: string) => `Your pause runs until ${d}. Your site stays live. We've emailed you the details.`,
    pausedBanner: (d: string) => `Your plan is paused until ${d}. Your site stays live. New work and reports pick up again the day after.`,
    /** Deck ruling c104: undo a pending cancel before the last day. */
    keepPlan: 'Keep my plan',
    keepPlanLead: (d: string) => `Changed your mind? Keep your plan any time before ${d} and nothing changes.`,
    kept: "You're keeping your plan. Nothing changes.",
  },
  prefs: {
    title: 'Notifications',
    lead: 'Choose how we tell you about each thing. Email always has a copy of anything important.',
    push: 'Alert',
    email: 'Email',
    quiet: 'Quiet hours',
    quietLead: "No alerts between these times, except new leads and your site going down.",
    quietFrom: 'From',
    quietTo: 'To',
    hideDetails: 'Hide details on the lock screen',
    hideDetailsDetail: 'Alerts say "New lead" without the name.',
    saved: 'Saved.',
    notAvailable: 'Not available for this',
    thisPhone: 'This phone',
  },
  security: {
    title: 'Sign in and devices',
    devices: 'Signed in devices',
    thisDevice: 'This device',
    lastUsed: (when: string, city: string | null) => (city ? `Last used ${when} in ${city}` : `Last used ${when}`),
    signOutDevice: 'Sign out',
    signOutAll: 'Sign out everywhere',
    signOutAllConfirm: 'Sign out on every phone and computer, including this one?',
    signedOutDevice: 'Signed out on that device.',
    passkeys: 'Face ID and fingerprint',
    passkeysLead: 'Sign in with your face or fingerprint instead of a code.',
    addPasskey: 'Add this phone',
    noPasskeys: "You haven't set this up yet.",
    removePasskey: 'Remove',
    email: 'Your sign in email',
    changeEmail: 'Change email',
    emailChangeLead: "We'll email the new address to confirm. The change takes effect 24 hours after, and we tell your old address too.",
    newEmail: 'New email',
    emailPending: (masked: string, d: string) => `Changing to ${masked} on ${d}.`,
    unknownDevice: 'Unknown device',
  },
  exportData: {
    title: 'Download your data',
    lead: "We'll put your leads, customer contacts, reviews, reports and account history into one file. It usually takes a few minutes. We'll email you when it's ready.",
    /** Deck ruling c013: the site files export is staff only. */
    siteNote: "Your website's files aren't in this download. The site is licensed to you while you're with us.",
    start: 'Start my download',
    building: "We're building your file. You can leave this screen. We'll email you when it's ready.",
    ready: 'Your file is ready.',
    download: (size: string) => `Download (${size})`,
    expires: (d: string) => `The link works until ${d}.`,
    failed: "We couldn't build your file. Nothing was lost. Try again, or send us a request.",
    onlyOwner: "Only the owner can download the business's data.",
  },
  help: {
    title: 'Help',
    search: 'Ask anything about your account',
    ask: 'Ask',
    handedOff: (by: string) => `I've passed this to Fletcher. He'll reply ${by}.`,
    openRequest: 'Open the request',
    sources: 'From',
    common: 'Common questions',
    faqs: [
      ['When do I get my report?', 'Every month, about 30 days after the last one. We send a push and an email when it lands.'],
      ['How do I change my hours?', 'Account, then Your hours. Change the days and tap Send. Sent before 3 pm Eastern, they go live the same day.'],
      ['Who owns my website?', "Your domain and Google profile are always yours. The website is licensed to you while you're with us. To keep it when you leave, ask us for a buyout quote. See Account, then What's yours."],
      ['How do review requests work?', 'Tap Ask for a review after a job. We send one email between 10 am and 7 pm their time and one reminder 3 days later if they haven’t opened it. You can change the words under Account, then Review emails.'],
      ['How do I cancel?', 'Account, then Billing, then Cancel my plan. It shows your exact last day before you confirm. Changed your mind? Tap Keep my plan before your last day.'],
      ['Can I pause instead?', 'Yes, once in any 12 months, for up to 30 days. We stop billing you and your site stays live. You’ll find it on the cancel screen.'],
    ] as [string, string][],
  },
  errors: {
    offlineSend: "You're offline. We couldn't send that. Nothing was lost. Try again when you're back online.",
    permission: (who: string) => `Only ${who} can do this.`,
    owner: 'the owner',
    sendFailed: "We couldn't send that. Nothing was lost. Check your connection and tap Send again.",
    saveFailed: "We couldn't save that. What you typed is still here. Try again in a minute.",
    sessionExpired: "You've been signed out. Sign in again and you'll come back to this screen.",
    signIn: 'Sign in',
    tooMany: "That's a lot of tries in a row. Wait a minute and try again.",
    locationOnly: (location: string) => `You can see ${location} only. The owner can change that in Team.`,
    frozen: "We're doing some upkeep on your account. You can read everything. Changes will open again within the hour.",
  },
  /** docs/52 §5: partial and stale data. */
  data: {
    stale: (when: string) => `Updated ${when}`,
    googleBehind: (d: string) => `Google's numbers haven't updated since ${d}. That's on Google's side.`,
    partial: (what: string) => `Some of this is missing: ${what}. We'll fill it in when it arrives.`,
    noComparison: 'Nothing to compare with yet. Next month shows the change.',
  },
  /** Payment problems (docs/52 §7.2, docs/44 44-D8) and the 7 day go-live credit (docs/41 §6.3). */
  payment: {
    failedBanner: (month: string) => `We couldn't take your ${month} payment. Your site's still running.`,
    pausedBanner: "We've paused new work until your card's updated. Your site and review replies keep running.",
    endingBanner: (d: string) => `Your card still hasn't gone through. Your plan ends on ${d} unless it's updated.`,
    update: 'Update card',
    goLiveMiss: "We missed your 7 day go-live, so your first month is on us. The credit's already on your account.",
    day90Refund: (amount: string) => `Your day 90 check qualifies for a refund of ${amount}. It's on its way to your card.`,
  },
  /** S43 bulk ask. */
  bulk: {
    title: 'Ask a whole list',
    lead: "Add today's customers in one go. We check every number before anything sends.",
    paste: 'Paste a list',
    pasteHint: 'One customer a line: first name, then email.',
    upload: 'Upload a spreadsheet',
    uploadHint: 'A CSV file with a first name and an email in each row.',
    photo: 'Take a photo of a job sheet',
    photoHint: 'We read the names and emails. You check them before anything sends.',
    reading: 'Reading your list',
    checked: (ok: number, total: number) => `${ok} of ${total} ready to send`,
    col: { name: 'Name', contact: 'Email', service: 'Service', status: 'Check' },
    flag: {
      ok: 'Ready',
      bad_number: "This number doesn't look right",
      landline: "It's a landline. We'll email instead if we have one",
      duplicate_list: 'Already in this list',
      asked_recently: (d: string) => `Asked on ${d}. We ask once a year at most`,
      opted_out: 'They asked us not to contact them',
      too_old: 'Too long ago to ask',
      needs_look: "We couldn't read this clearly. Check it",
    },
    pacing: (perDay: number) => `We'll send ${perDay} a day so they go out at a steady pace.`,
    held: "Some of these need a look from our team first. We'll tell you once they're cleared.",
    send: (count: number) => `Send ${n(count, 'request', 'requests')}`,
    sent: (count: number) => `${n(count, 'request', 'requests')} scheduled.`,
    empty: 'Nothing to send yet. Add a list above.',
    // docs/54 B23: requests read from forwarded invoices wait 2 hours so the owner can stop a wrong one.
    invoiceHold: (count: number, time: string) => `We found ${n(count, 'customer', 'customers')} in today's invoices. We'll ask them at ${time} unless you stop one.`,
    invoiceCheck: "We couldn't read this one clearly. Check the name and number before it sends.",
    stopOne: 'Stop this one',
    stopped: "Stopped. We won't ask them.",
  },
  /** S45 review card. */
  qr: {
    title: 'Your review card',
    lead: 'Hand one over at the end of a job. The code opens your Google review page.',
    download: 'Download the print file',
    mail: 'Mail me 50 cards',
    mailed: "We'll mail your cards this week.",
    preview: 'Your card',
  },
  /** S71 holiday prompts. */
  holiday: {
    title: (holiday: string) => `Are you open on ${holiday}?`,
    lead: 'We update your website and your Google profile so nobody turns up to a locked door.',
    closed: 'Closed',
    normal: 'Normal hours',
    different: 'Different hours',
    saved: (holiday: string) => `Got it. Your ${holiday} hours will show on Google and your site.`,
  },
  /** S72 emergency banner templates. Each is built from their facts, never typed free. */
  bannerTemplates: {
    emergency: (phone: string) => `We're taking emergency calls today. Call ${phone}.`,
    freeze: (phone: string) => `Freeze warning: we can help with frozen pipes. Call ${phone}.`,
    closedToday: (time: string) => `We're closed today. We'll be back tomorrow at ${time}.`,
    remove: 'Take the banner down',
    removed: "It'll come off your site within the hour.",
  },
  /** S82 day 90 result. */
  day90: {
    title: 'Your day 90 check',
    base: (v: string) => `Day one: in the top three on ${v} of 81 points (the average of two scans).`,
    now: (v: string) => `Day 90: ${v} of 81.`,
    met: "That's up, so the promise is met.",
    flat: "That's not up. The promise wasn't met, so you can have your money back.",
    working: 'Show the working',
    refund: 'Get my refund',
    refundDone: "Your refund is on its way to your card. We've emailed you the details.",
    jobs: (count: number) => `Jobs you sent us in the 90 days: ${count}`,
    /** The read as computed (c009, c010, r01). Month three back is our team's decision, so nothing here says it's paid. */
    outcome: { kept: 'Promise met', missed_eligible: 'Not up. You can ask for month three back', missed_not_eligible: 'Not up' } as Record<'kept' | 'missed_eligible' | 'missed_not_eligible', string>,
    services: 'Your three services',
    servicesCaption: 'Points in the top three of 81, on day one and on day 90',
    service: 'Service',
    dayOne: 'Day one',
    dayNinety: 'Day 90',
    habitTitle: 'Your two jobs',
    habit: (weeks: number, months: number) => `You asked for reviews in ${weeks} of 12 weeks and added job photos in ${months} of 3 months.`,
    habitNeeds: 'The two jobs count as done at 10 weeks and 2 months. Weeks and months you marked as no jobs count as kept.',
    late: (days: number) => `Your day 90 scans ran ${n(days, 'day', 'days')} late because your plan was paused on day 90.`,
  },
  /** Profile and reviews mode: the site module is off for this account (docs/50 §2.6). */
  siteOff: {
    note: "We look after your Google profile and reviews. Your website isn't part of your plan.",
  },
  locations: {
    title: 'Locations',
    lead: 'Each location has its own Google profile, reviews and leads.',
    all: 'All locations',
    add: 'Add a location',
    addLead: "We'll walk you through it. It takes about 10 minutes, then we add its page to your site.",
    scoped: (count: number) => `Can see ${n(count, 'location', 'locations')}`,
  },
};

export type Strings = typeof S;
